import Select from "react-select";
import React, {useEffect, useRef, useState} from "react";
import {useProviderOptions} from "../../store/actions/providerActions";
import {useCustomerOptions} from "../customer";
import styled from "styled-components";
import {Modal} from "react-bootstrap";
import ProviderAddOrUpdateContent from "../../components/content/providerAddOrUpdateContent";
import CustomerAddOrUpdateContent from "../../components/content/customerAddOrUpdateContent";
import {verifyRight} from "../users";
import {detailedVehicleOptions, getFilteredDetailedVehicleOptions} from "../vehicles";

const ClientsInfosContainer = styled.div`
    border: 1px solid hsl(0, 0%, 80%);
    border-top: none;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    position: relative;
    top: -5px;
    padding: 12px 8px 8px 8px;
    display: flex;
    flex-direction: column;
    row-gap: 2px;

    p {
        margin: 0;
        padding: 0;
    }
`

export default function FormPassenger({
                                        userInfos, isLoaded, booking, folder, setBookingField, handleChange,
                                      }) {
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [providerReloadToken, setProviderReloadToken] = useState(0);
  const [customerReloadToken, setCustomerReloadToken] = useState(0);
  
  const filteredVehicleOptions = getFilteredDetailedVehicleOptions(userInfos);

  let providerOptions = useProviderOptions(true, "Aucun", providerReloadToken);
  const customerOptions = useCustomerOptions(customerReloadToken);
  const customerSelectRef = useRef(null);

  const filteredCustomerOptions = customerOptions.filter((customerOption) => {
    if (!booking?.provider?.id) return true;

    return customerOption?.data?.provider?.id === booking.provider.id;
  });

  function providerChanged(providerOption) {
    setBookingField("provider", providerOption.data);
    // Set provider tariff automatically if available, otherwise reset
    if (providerOption.data?.tarif) {
      setBookingField("tarif", providerOption.data.tarif);
    } else {
      setBookingField("tarif", "");
      // Reset prices when tarif is cleared
      setBookingField("driver_price", 0);
      setBookingField("amount", 0);
      setBookingField("distance", "");
      setBookingField("duration", "");
    }
    setCustomer(null);
    customerSelectRef.current.select.clearValue();
    setBookingField("passengers", ""); // Reset passengers when provider changes
  }

  useEffect(() => {
    if (userInfos?.child?.rights?.providersFilter?.length && providerOptions?.length) {
      setBookingField("provider", providerOptions[0].data);
    }
  }, [userInfos, providerOptions, setBookingField]);

  function setCustomer(obj) {
    // If obj is null or undefined, reset client fields
    if (!obj) {
      setBookingField("customer_data", {});
      setBookingField("firstname", "");
      setBookingField("lastname", "");
      setBookingField("phone", "");
      setBookingField("email", "");
      setBookingField("passengers", ""); // Reset passengers when customer is cleared
      return;
    }
    
    const customer = obj?.data;

    if (customer?.provider?.id) {
      const provider = providerOptions.find((p) => p?.data?.id === customer.provider.id);
      
      if (provider) {
        setBookingField("provider", provider.data);
        // Set provider tariff automatically if available, otherwise reset
        if (provider.data?.tarif) {
          setBookingField("tarif", provider.data.tarif);
        } else {
          setBookingField("tarif", "");
        }
      }
    } else {
      // If customer has no associated provider, reset tariff
      setBookingField("tarif", "");
    }

    setBookingField("customer_data", customer);
    setBookingField("firstname", customer?.firstname ?? "");
    setBookingField("lastname", customer?.lastname ?? "");
    setBookingField("phone", customer?.phone ?? "");
    setBookingField("email", customer?.email ?? "");
    setBookingField("passengers", ""); // Reset passengers when customer changes
  }

  function onProviderCreated(provider) {
    // Select the newly created provider and refresh options
    setShowProviderModal(false);
    setProviderReloadToken((t) => t + 1);
    setBookingField("provider", provider);
    // Clear current passenger selection since provider changed
    setCustomer(null);
    try {
      customerSelectRef.current?.select?.clearValue();
    } catch (e) {
    }
  }

  function onCustomerCreated(customer) {
    // Refresh list and select the newly created customer
    setShowCustomerModal(false);
    setCustomerReloadToken((t) => t + 1);
    setCustomer({data: customer});
  }

  if (!isLoaded) return (<div className="kt-section kt-section--first">
    <p>Chargement...</p>
  </div>);

  return (<div className="kt-section kt-section--first">
    <div className="kt-section__body">
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Client</label>
        <div className="col-lg-6">
          <Select
            options={providerOptions}
            value={booking?.provider?.id ? {
              value: booking.provider.id, label: booking.provider.name, data: booking.provider,
            } : null}
            onChange={providerChanged}
            isDisabled={booking.sharedBy || folder}
          />
          {booking?.provider?.id && <ClientsInfosContainer>
            <p>
              <strong>{booking?.provider?.name}</strong>
            </p>
            <p>
              <strong>Email :</strong> {booking?.provider?.email || "N/A"}
            </p>
            <p>
              <strong>Tel :</strong> {booking?.provider?.tel || "N/A"}
            </p>
          </ClientsInfosContainer>}
          {!(booking.sharedBy || folder) && verifyRight(userInfos, "provider", "create") && (<div className="mt-2">
            <button type="button" className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowProviderModal(true)}>
              Ajouter un client
            </button>
          </div>)}
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Passager *</label>
        <div className="col-lg-6">
          <Select
            ref={customerSelectRef}
            options={filteredCustomerOptions}
            value={booking?.customer_data?.id ? {
              value: booking.customer_data.id,
              label: booking.customer_data.firstname + " " + booking.customer_data.lastname,
              data: booking.customer_data,
            } : null}
            onChange={(obj) => setCustomer(obj)}
            isDisabled={booking.sharedBy}
          />
          {(booking?.firstname || booking?.lastname || booking?.phone || booking?.email) && <ClientsInfosContainer>
            <p>
              <strong>{booking?.firstname} {booking?.lastname}</strong>
            </p>
            <p>
              <strong>Email :</strong> {booking?.email || "N/A"}
            </p>
            <p>
              <strong>Tel :</strong> {booking?.phone || "N/A"}
            </p>
          </ClientsInfosContainer>}
          {!booking.sharedBy && verifyRight(userInfos, "customer", "create") && (<div className="mt-2">
            <button type="button" className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowCustomerModal(true)}>
              Ajouter un passager
            </button>
          </div>)}
        </div>
      </div>
      {booking.id && <div className="form-group row">
        <label className="col-lg-3 col-form-label">Email de contact:</label>
        <div className="col-lg-6">
          <input
            type="email"
            className="form-control"
            value={booking.contact_email}
            placeholder={booking.email}
            name="contact_email"
            onChange={handleChange}
            disabled={booking.sharedBy}
          />
        </div>
      </div>}
      {booking.id && <div className="form-group row">
        <label className="col-lg-3 col-form-label">
          Téléphone de contact:
        </label>
        <div className="col-lg-6">
          <input
            type="tel"
            className="form-control"
            value={booking.contact_phone}
            placeholder={booking.phone}
            name="contact_phone"
            onChange={handleChange}
            disabled={booking.sharedBy}
          />
        </div>
      </div>}
      {booking.ref_code && <div className="form-group row">
        <label className="col-lg-3 col-form-label">
          Code référence client:
        </label>
        <div className="col-lg-6">
          <input
            type="text"
            value={booking.ref_code}
            className="form-control"
            name="ref_code"
            onChange={handleChange}
            disabled={booking.sharedBy}
          />
        </div>
      </div>}
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Numéro de vol / train:</label>
        <div className="col-lg-6">
          <input
            type="text"
            className="form-control"
            name="flight_number"
            value={booking.flight_number}
            onChange={handleChange}
            disabled={booking.sharedBy}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">
          Nombre de passagers *
        </label>
        <div className="col-lg-6">
          <input
            type="number"
            className="form-control"
            name="passengers"
            value={booking.passengers}
            onChange={handleChange}
            disabled={booking.sharedBy}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Nombre de bagages:</label>
        <div className="col-lg-6">
          <input
            type="number"
            className="form-control"
            name="bags"
            value={booking.bags}
            onChange={handleChange}
            disabled={booking.sharedBy}
          />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-lg-3 col-form-label">Véhicule *</label>
        <div className="col-lg-6">
          <select
            name="vehicle"
            className="form-control"
            onChange={handleChange}
            disabled={booking.sharedBy}
          >
            <option value="" selected={booking.vehicle === ""}>
              Sélectionnez le véhicule
            </option>
            {filteredVehicleOptions.map((vehicle) => (
              <option
                key={vehicle.value}
                value={vehicle.value}
                selected={booking.vehicle === vehicle.value}
              >
                {vehicle.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>

    {/* Provider Modal */}
    <Modal show={showProviderModal} onHide={() => setShowProviderModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Créer un client</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ProviderAddOrUpdateContent onCreated={onProviderCreated}/>
      </Modal.Body>
    </Modal>

    {/* Customer Modal */}
    <Modal show={showCustomerModal} onHide={() => setShowCustomerModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Créer un passager</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <CustomerAddOrUpdateContent onCreated={onCustomerCreated}/>
      </Modal.Body>
    </Modal>
  </div>);
}
